/* fc-cart-qty.js — [−] N in cart [+] counter for "Add to cart" slots.

   WHAT
   A "slot" is any element with `data-fc-qty-slot` that holds an Add to cart button and a
   hidden counter (snippets/fc-qty-counter.liquid): the grid card's <product-form>, and the
   pinned product-page buy bar (snippets/fc-sticky-buy.liquid). While the slot's variant is
   in the cart, the button is swapped for the counter showing the real line quantity.

   WHY
   A grid add used to flash "Added" for 2.5 s and reset to "Add to cart"; a second tap
   silently added another and nothing showed how many were in the cart (87% of shoppers are
   on phones, where the cart drawer is a tap away but out of sight).

   HOW
   - Which variant: the `input[name="id"]` of the slot's form — the form inside the slot, or
     the one named by `data-fc-qty-form` (the bar points at the main product form).
   - State comes from /cart.js: once on load (and on back/forward-cache restore), then kept
     current from every `PUB_SUB_EVENTS.cartUpdate` the theme publishes (card adds, cart
     drawer steppers and removals, helium page adds).
   - +/− call /cart/change.js with the line `key`. Taps are coalesced per variant and every
     cart request runs through one queue, so requests never overlap.
   - After each change the cart drawer and header count bubble are re-rendered from the
     Section Rendering API response (same sections cart-drawer.js uses), without opening
     the drawer. The theme's cartUpdate event is then published with source 'fc-cart-qty'.
   - Refused or capped changes show "Only N left" (the existing fc_low_stock_left string) in
     the slot's `.fc-card-error` wrapper.
   The Add to cart itself is untouched: it still goes through assets/product-form.js. */
(function () {
  'use strict';
  if (window.FCCartQty) return;

  var SLOT = '[data-fc-qty-slot]';
  var strings = readStrings();
  var lines = new Map(); // variantId -> { key, quantity, props }
  var pending = new Map(); // variantId -> quantity requested but not yet confirmed
  var inflight = new Set(); // variantIds with a running change loop
  var queue = Promise.resolve();
  var synced = false;
  // Bumped whenever `lines` takes data newer than any /cart.js read that may be in flight
  // (an add, a drawer change, a +/− response). A read that started before a bump is stale
  // and is dropped, so it can't flip a card back to "Add to cart" and invite a double add.
  var gen = 0;
  var syncTimer = null;
  var live = null;
  var moQueued = false;

  function readStrings() {
    var fallback = { inCart: '__Q__', lowOne: '', lowOther: '' };
    try {
      var el = document.getElementById('FcCartQtyStrings');
      return el ? Object.assign(fallback, JSON.parse(el.textContent)) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function changeUrl() {
    return (
      (window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : root() + 'cart/change') + '.js'
    );
  }

  // One queue for every cart request this file makes.
  function enqueue(fn) {
    var run = queue.then(fn);
    queue = run.catch(function () {});
    return run;
  }

  /* ---------- slots ---------- */

  function slotForm(slot) {
    var id = slot.getAttribute('data-fc-qty-form');
    return id ? document.getElementById(id) : slot.querySelector('form');
  }

  function slotVariant(slot) {
    var form = slotForm(slot);
    var input = form && form.querySelector('input[name="id"]');
    return input && input.value ? String(input.value) : '';
  }

  function addButton(slot) {
    return slot.querySelector('[data-fc-qty-add]') || slot.querySelector('button[type="submit"][name="add"]');
  }

  function counterOf(slot) {
    return slot.querySelector('[data-fc-qty]');
  }

  function slotsFor(variantId) {
    return Array.prototype.filter.call(document.querySelectorAll(SLOT), function (slot) {
      return slotVariant(slot) === variantId;
    });
  }

  function shownQty(variantId) {
    if (!variantId) return 0;
    if (pending.has(variantId)) return pending.get(variantId);
    var line = lines.get(variantId);
    return line ? line.quantity : 0;
  }

  // The counter takes the look of the button it replaces, so it follows the palette and any
  // per-section button colours without this file naming a colour. The grid's colours come
  // from a <style> inside the section, which lands after the cards and runs Cello's
  // `transition: all` on the button — so wait for running transitions (and for the pointer
  // to leave) before sampling, or the counter freezes a half-faded or hover colour.
  function mirrorColors(slot, add) {
    if (!slot.isConnected || slot.hasAttribute('data-fc-qty-colors') || slot.fcQtySampling) return;
    var running = add.getAnimations
      ? add.getAnimations().filter(function (a) {
          return a.playState === 'running';
        })
      : [];
    if (running.length || add.matches(':hover')) {
      slot.fcQtySampling = true;
      Promise.all(
        running.map(function (a) {
          return a.finished.catch(function () {});
        })
      )
        .then(function () {
          return new Promise(function (resolve) {
            setTimeout(resolve, running.length ? 50 : 1500);
          });
        })
        .then(function () {
          slot.fcQtySampling = false;
          mirrorColors(slot, add);
        });
      return;
    }
    var cs = window.getComputedStyle(add);
    slot.style.setProperty('--fc-qty-fg', cs.color);
    slot.style.setProperty('--fc-qty-bg', cs.backgroundColor);
    slot.style.setProperty('--fc-qty-border', parseFloat(cs.borderTopWidth) > 0 ? cs.borderTopColor : cs.color);
    slot.setAttribute('data-fc-qty-colors', '');
  }

  function setCount(counter, qty) {
    var el = counter.querySelector('[data-fc-qty-count]');
    if (!el || el.getAttribute('data-q') === String(qty)) return;
    el.setAttribute('data-q', String(qty));
    el.textContent = '';
    var parts = strings.inCart.split('__Q__');
    if (parts.length !== 2) {
      el.textContent = strings.inCart.replace('__Q__', qty);
      return;
    }
    if (parts[0].trim()) el.appendChild(document.createTextNode(parts[0]));
    var num = document.createElement('span');
    num.className = 'fc-qty__num';
    num.textContent = qty;
    el.appendChild(num);
    if (parts[1].trim()) {
      var label = document.createElement('span');
      label.className = 'fc-qty__label';
      label.textContent = parts[1].trim();
      el.appendChild(label);
    }
  }

  function paint(slot) {
    var counter = counterOf(slot);
    var add = addButton(slot);
    if (!counter || !add) return;
    mirrorColors(slot, add);

    var variantId = slotVariant(slot);
    var qty = shownQty(variantId);
    var on = qty > 0;
    var busy = inflight.has(variantId);

    if (on) {
      setCount(counter, qty);
      var one = qty === 1;
      counter.classList.toggle('fc-qty--one', one);
      var minus = counter.querySelector('[data-fc-qty-action="dec"]');
      if (minus) minus.setAttribute('aria-label', minus.getAttribute(one ? 'data-label-rem' : 'data-label-dec'));
    }
    counter.classList.toggle('is-busy', busy);
    counter.setAttribute('aria-busy', busy ? 'true' : 'false');

    if (on === !counter.hidden) return;
    // Move focus with the swap so keyboard and screen-reader users don't fall back to <body>.
    var active = document.activeElement;
    var hadFocus = active && (active === add || counter.contains(active));
    counter.hidden = !on;
    add.classList.toggle('fc-qty-hide', on);
    slot.classList.toggle('fc-qty-active', on);
    if (hadFocus) {
      var next = on ? counter.querySelector('[data-fc-qty-action="inc"]') : add;
      if (next && !next.disabled) next.focus({ preventScroll: true });
    }
  }

  function paintAll() {
    document.querySelectorAll(SLOT).forEach(paint);
  }

  /* ---------- messages ---------- */

  function ensureLive() {
    if (live) return live;
    live = document.createElement('div');
    live.className = 'visually-hidden';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    document.body.appendChild(live);
    return live;
  }

  function announce(qty) {
    var region = ensureLive();
    region.textContent = '';
    setTimeout(function () {
      region.textContent = strings.inCart.replace('__Q__', qty);
    }, 80);
  }

  function errorBox(slot) {
    return slot.querySelector('.fc-card-error') || slot.querySelector('.product-form__error-message-wrapper');
  }

  function showMessage(slot, text) {
    var box = slot && errorBox(slot);
    if (!box || !text) return;
    var msg = box.querySelector('.product-form__error-message') || box;
    msg.textContent = text;
    box.hidden = false;
  }

  function hideMessage(slot) {
    var box = errorBox(slot);
    if (box) box.hidden = true;
  }

  function capText(available) {
    if (available <= 0) return (window.variantStrings && window.variantStrings.soldOut) || '';
    if (available === 1 && strings.lowOne) return strings.lowOne;
    return strings.lowOther.replace('7919', String(available));
  }

  function errorText() {
    return (window.cartStrings && window.cartStrings.error) || '';
  }

  /* ---------- cart state ---------- */

  function hasProps(item) {
    var p = item && item.properties;
    if (!p) return false;
    return Object.keys(p).some(function (k) {
      return p[k] !== '' && p[k] !== null && p[k] !== undefined;
    });
  }

  // A variant can sit on several lines (e.g. with line-item properties). The counter drives
  // the plain line when there is one, otherwise the first.
  function buildLines(cart) {
    var next = new Map();
    cart.items.forEach(function (item) {
      var id = String(item.variant_id);
      var props = hasProps(item);
      var current = next.get(id);
      if (!current || (current.props && !props)) next.set(id, { key: item.key, quantity: item.quantity, props: props });
    });
    return next;
  }

  // A whole cart that is at least as new as anything we hold (drawer change, +/− response).
  function applyCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    gen++;
    lines = buildLines(cart);
    synced = true;
    paintAll();
  }

  // Only this variant's entry from a response that may predate another change (see run()).
  function applyVariant(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return;
    var line = buildLines(cart).get(variantId);
    gen++;
    if (line) lines.set(variantId, line);
    else lines.delete(variantId);
    synced = true;
    paintAll();
  }

  function fetchCart() {
    return fetch(root() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(
      function (r) {
        return r.json();
      }
    );
  }

  function sync() {
    return enqueue(function () {
      var startedAt = gen;
      return fetchCart().then(function (cart) {
        if (gen !== startedAt) {
          scheduleSync(300); // something newer landed while we read; read again later
          return;
        }
        lines = buildLines(cart);
        synced = true;
        paintAll();
      });
    }).catch(function (e) {
      console.error('fc-cart-qty: could not read the cart', e);
    });
  }

  function scheduleSync(ms) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, ms || 0);
  }

  /* ---------- drawer + header count ---------- */

  function sectionIds() {
    if (document.querySelector('cart-drawer')) return ['cart-drawer', 'cart-icon-bubble'];
    return document.getElementById('cart-icon-bubble') ? ['cart-icon-bubble'] : [];
  }

  // Same render path as a card add (product-form.js -> cart-drawer.js renderContents), with
  // the drawer kept closed, plus the empty-state class cart.js toggles.
  function renderSections(cart) {
    if (!cart || !cart.sections) return;
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function' && cart.sections['cart-drawer']) {
      try {
        drawer.renderContents(cart, { open: false });
      } catch (e) {
        console.error(e);
      }
      drawer.classList.toggle('is-empty', cart.item_count === 0);
      return;
    }
    var bubble = document.getElementById('cart-icon-bubble');
    var html = cart.sections['cart-icon-bubble'];
    if (!bubble || !html) return;
    var src = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (src) bubble.innerHTML = src.innerHTML;
  }

  function publishUpdate(cart) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fc-cart-qty', cartData: cart });
  }

  /* ---------- +/− ---------- */

  function postChange(key, quantity) {
    var body = { id: key, quantity: quantity };
    var sections = sectionIds();
    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    return fetch(changeUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (json) {
        return { ok: r.ok && !json.status && !json.errors, cart: json };
      });
    });
  }

  function step(slot, delta) {
    var variantId = slotVariant(slot);
    if (!variantId || !lines.has(variantId)) return;
    pending.set(variantId, Math.max(0, shownQty(variantId) + delta));
    hideMessage(slot);
    slotsFor(variantId).forEach(paint);
    if (!inflight.has(variantId)) run(variantId, slot);
  }

  // Sends the latest requested quantity for one variant, then keeps going while taps arrive
  // during a request. Stops early (and says why) when Shopify caps or refuses the change.
  // The loop is pinned to the line it started on: if that line disappears (trash to 0) and
  // the variant still sits on another line, it stops instead of emptying that one too.
  function run(variantId, slot) {
    var changed = null;
    var pinnedKey = null;
    inflight.add(variantId);
    slotsFor(variantId).forEach(paint);

    function stop() {
      pending.delete(variantId);
      return Promise.resolve();
    }

    function loop() {
      var line = lines.get(variantId);
      if (!pending.has(variantId) || !line) return stop();
      if (pinnedKey === null) pinnedKey = line.key;
      else if (line.key !== pinnedKey) return stop();
      var target = pending.get(variantId);
      if (target === line.quantity) return stop();
      var sentAt;
      return enqueue(function () {
        sentAt = gen;
        return postChange(line.key, target);
      }).then(function (res) {
        if (res.ok) {
          changed = res.cart;
          // An add or drawer change that landed while this request was out may be missing
          // from this response: then take only this variant from it and re-read the rest.
          if (gen === sentAt) {
            applyCart(res.cart);
          } else {
            applyVariant(res.cart, variantId);
            scheduleSync(300);
          }
          renderSections(res.cart);
          var now = lines.get(variantId);
          var kept = now && now.key === pinnedKey ? now.quantity : 0;
          if (kept < target && pending.get(variantId) === target) {
            pending.delete(variantId);
            showMessage(slot, capText(kept));
            return;
          }
          return loop();
        }
        // Refused (e.g. 422 "Only 1 item was added…"): read back what Shopify kept.
        pending.delete(variantId);
        return enqueue(function () {
          var readAt = gen;
          return fetchCart().then(function (cart) {
            if (gen === readAt) applyCart(cart);
            else applyVariant(cart, variantId);
            changed = cart;
            var now = lines.get(variantId);
            var kept = now && now.key === pinnedKey ? now.quantity : 0;
            showMessage(slot, kept < target ? capText(kept) : errorText());
          });
        });
      });
    }

    loop()
      .catch(function (e) {
        console.error('fc-cart-qty: change failed', e);
        pending.delete(variantId);
        showMessage(slot, errorText());
        return sync();
      })
      .then(function () {
        inflight.delete(variantId);
        if (changed) {
          if (!changed.sections) refreshSections();
          publishUpdate(changed);
        }
        // Taps made while a refused change was being read back (or while a failed request
        // re-synced) are still waiting: send them rather than leave them painted but unsent.
        var line = lines.get(variantId);
        if (pending.has(variantId)) {
          if (line && pending.get(variantId) !== line.quantity) {
            run(variantId, slot);
            return;
          }
          pending.delete(variantId);
        }
        slotsFor(variantId).forEach(paint);
        announce(shownQty(variantId));
      });
  }

  // After a refused change there is no sections payload; fetch the two sections directly.
  function refreshSections() {
    var ids = sectionIds();
    if (!ids.length) return;
    fetch(root() + '?sections=' + ids.join(','), { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        return fetchCart().then(function (cart) {
          cart.sections = sections;
          renderSections(cart);
        });
      })
      .catch(function () {});
  }

  /* ---------- wiring ---------- */

  function onCartUpdate(event) {
    if (!event || event.source === 'fc-cart-qty') return;
    var data = event.cartData;
    if (data && Array.isArray(data.items) && typeof data.item_count === 'number') {
      applyCart(data); // cart drawer steppers / removals send the whole cart
      return;
    }
    gen++; // any /cart.js read already in flight predates this change
    if (data && data.key && data.variant_id && !hasProps(data)) {
      // A single-item /cart/add.js response (card or product-page add). Its `quantity` is
      // the line's TOTAL after the add (verified 2026-09-24: adds of 2, 1, 3 returned 2, 3,
      // 6), so it can be shown as-is.
      var id = String(data.variant_id);
      lines.set(id, { key: data.key, quantity: data.quantity, props: false });
      synced = true;
      paintAll();
      if (slotsFor(id).length) announce(data.quantity);
    }
    scheduleSync(300);
  }

  // A refused add can still have added part of the quantity (422 "Only 5 items were added to
  // your cart due to availability."), and product-form.js publishes no cartUpdate for it.
  // Re-read so the counters, drawer and header count show what Shopify actually kept.
  function onCartError() {
    sync().then(refreshSections);
  }

  function initSlots() {
    var fresh = document.querySelectorAll(SLOT + ':not([data-fc-qty-ready])');
    if (!fresh.length) return;
    fresh.forEach(function (slot) {
      slot.setAttribute('data-fc-qty-ready', '');
      paint(slot);
    });
    if (!synced) scheduleSync(0);
  }

  function init() {
    ensureLive();
    initSlots();

    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-fc-qty-action]');
      if (!btn) return;
      var slot = btn.closest(SLOT);
      if (!slot) return;
      e.preventDefault();
      step(slot, btn.getAttribute('data-fc-qty-action') === 'inc' ? 1 : -1);
    });

    // Variant switches (card pickers, product page) change the id input; repaint for it.
    document.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches('input[name="id"]')) paintAll();
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
      subscribe(PUB_SUB_EVENTS.cartError, onCartError);
    }

    // Back/forward cache restores the page as it was; the cart may have moved on since.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      pending.clear();
      sync();
    });

    // Filtering, "load more" and the cart drawer re-render cards; pick up the new ones.
    new MutationObserver(function () {
      if (moQueued) return;
      moQueued = true;
      window.requestAnimationFrame(function () {
        moQueued = false;
        initSlots();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.FCCartQty = {
    sync: sync,
    paint: paintAll,
    quantity: shownQty,
    variantOf: slotVariant,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
